/** Heuristic resale estimator — ported 1:1 from the mobile CarValuationEstimator. */
import type { Car, FuelLog } from "../model.ts";

export interface ValuationEstimate {
  currentValue: number;
  purchasePrice: number | null;
  totalLoss: number | null;
  depreciationPct: number | null;
  ageYears: number;
  odometer: number | null;
  totalSpent: number;
  subtitle: string;
}

/** `totalSpent` is passed in (the web ledger total for the car) so it stays
 *  consistent with the SPEND screen; everything else mirrors the phone. */
export function estimateValuation(car: Car, fuelLogs: FuelLog[], totalSpent: number): ValuationEstimate {
  const nowSec = Date.now() / 1000;
  const ageYears = car.purchaseDate != null
    ? Math.max(0, (nowSec - car.purchaseDate) / (86400 * 365.25))
    : Math.max(0, new Date().getUTCFullYear() - (car.year ?? new Date().getUTCFullYear()));

  const odometer = fuelLogs.length ? Math.max(...fuelLogs.map((l) => l.odometer)) : null;
  const purchaseOdo = car.purchaseOdometer ?? 0;
  const distanceSincePurchase = odometer != null ? Math.max(0, odometer - purchaseOdo) : 0;

  let retention = 1, remaining = ageYears;
  if (remaining > 0) { const y = Math.min(remaining, 1); retention *= Math.pow(0.80, y); remaining -= y; }
  if (remaining > 0) { const y = Math.min(remaining, 4); retention *= Math.pow(0.88, y); remaining -= y; }
  if (remaining > 0) { retention *= Math.pow(0.92, remaining); }
  if (odometer != null) {
    const expectedKm = ageYears * 15000;
    const overKm = Math.max(0, distanceSincePurchase - expectedKm);
    const penalty = Math.min(0.35, (overKm / 10000) * 0.03);
    retention *= (1 - penalty);
  }
  retention = Math.max(0.10, retention);

  const price = car.purchasePrice ?? null;
  let currentValue = 0, totalLoss: number | null = null, depreciationPct: number | null = null;
  if (price != null && price > 0) {
    currentValue = price * retention;
    totalLoss = Math.max(0, price - currentValue);
    depreciationPct = (1 - retention) * 100;
  }

  const yearsText = ageYears < 1 ? `${Math.round(ageYears * 12)} mo` : `${ageYears.toFixed(1)} yr`;
  const subtitle = price != null ? `Age ${yearsText} · spend included below` : "Add purchase price for a real estimate";

  return { currentValue, purchasePrice: price, totalLoss, depreciationPct, ageYears, odometer, totalSpent, subtitle };
}

/** Retained fraction 0..1 for the depreciation bar. */
export function retainedFraction(e: ValuationEstimate): number {
  if (e.depreciationPct == null) return 0;
  return Math.min(1, Math.max(0, 1 - e.depreciationPct / 100));
}
